// =============================================================================
// MeuDriver — InstructorPayoutCard
// Chave Pix de recebimento + repasses (a liberar, disponíveis, pagos).
// Não há "solicitar saque": o repasse de cada aula consumida é pago na chave
// cadastrada depois da janela pós-aula. Regras em src/hooks/useEarnings.ts.
// =============================================================================

import { useEffect, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import { Banknote, KeyRound, Clock, CheckCircle2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatCurrency } from '@/lib/utils'
import {
  EARNING_REASON_LABELS, PIX_KEY_LABELS, type PixKeyType,
  useMyEarnings, usePayoutAccount, useSavePixKey,
} from '@/hooks/useEarnings'

const KEY_TYPES = Object.keys(PIX_KEY_LABELS) as PixKeyType[]

export function InstructorPayoutCard() {
  const { data: account, isLoading: loadingAccount } = usePayoutAccount()
  const { data: earnings, isLoading: loadingEarnings } = useMyEarnings()
  const save = useSavePixKey()

  const [editing, setEditing] = useState(false)
  const [type, setType] = useState<PixKeyType>('cpf')
  const [key, setKey] = useState('')
  const [holder, setHolder] = useState('')

  useEffect(() => {
    if (account) {
      setType(account.pix_key_type)
      setKey(account.pix_key)
      setHolder(account.holder_name)
    }
  }, [account])

  const now = Date.now()
  const list = earnings ?? []
  const aLiberar = list.filter((e) => e.status === 'pending' && new Date(e.available_at).getTime() > now)
  const disponivel = list.filter((e) => (e.status === 'pending' && new Date(e.available_at).getTime() <= now) || e.status === 'processing')
  const pago = list.filter((e) => e.status === 'paid')
  const soma = (xs: typeof list) => xs.reduce((s, e) => s + e.amount, 0)

  async function salvar() {
    try {
      await save.mutateAsync({ type, key, holder })
      toast.success('Chave Pix salva. Os repasses vão para ela.')
      setEditing(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao salvar.')
    }
  }

  const showForm = editing || (!loadingAccount && !account)

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Banknote className="h-4 w-4 text-brand-600" />
          Recebimento
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Resumo */}
        <div className="grid gap-3 sm:grid-cols-3">
          <Resumo icon={<Clock className="h-4 w-4 text-amber-600" />} label="A liberar" valor={soma(aLiberar)}
            hint="Aulas consumidas, dentro da janela de segurança pós-aula." />
          <Resumo icon={<Banknote className="h-4 w-4 text-brand-600" />} label="Disponível para repasse" valor={soma(disponivel)}
            hint="Será pago na sua chave Pix." />
          <Resumo icon={<CheckCircle2 className="h-4 w-4 text-green-600" />} label="Já recebido" valor={soma(pago)} />
        </div>

        {/* Chave Pix */}
        <div className="rounded-xl border border-border/60 p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Chave Pix para receber
          </p>
          {loadingAccount ? (
            <Skeleton className="h-10 w-full" />
          ) : showForm ? (
            <div className="space-y-3">
              {!account && (
                <p className="text-xs text-amber-700">
                  Cadastre a chave para receber seus repasses. Se você tem conta no Mercado Pago,
                  pode usar a chave Pix dela.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Prefira chave CPF ou CNPJ: é o tipo que permite o repasse automático. Com os outros, o pagamento é feito manualmente.
              </p>
              <div className="flex flex-wrap gap-2">
                {KEY_TYPES.map((t) => (
                  <button key={t} type="button" onClick={() => setType(t)}
                    className={cn('rounded-lg border px-3 py-1.5 text-xs font-medium',
                      type === t ? 'border-primary bg-primary text-white' : 'border-gray-200 text-gray-700 hover:border-primary')}>
                    {PIX_KEY_LABELS[t]}
                  </button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="pix-key">Chave ({PIX_KEY_LABELS[type]})</Label>
                  <Input id="pix-key" value={key} onChange={(e) => setKey(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pix-holder">Nome do titular</Label>
                  <Input id="pix-holder" value={holder} onChange={(e) => setHolder(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" disabled={save.isPending || !key || !holder} onClick={salvar}>
                  {save.isPending ? 'Salvando...' : 'Salvar chave'}
                </Button>
                {account && (
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-gray-700">
                {PIX_KEY_LABELS[account!.pix_key_type]}: <strong>{account!.pix_key}</strong>
                <span className="text-muted-foreground"> · {account!.holder_name}</span>
              </p>
              <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>Alterar</Button>
            </div>
          )}
        </div>

        {/* Lista */}
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-2">Últimos repasses</p>
          {loadingEarnings ? (
            <Skeleton className="h-16 w-full" />
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma aula consumida ainda.</p>
          ) : (
            <ul className="divide-y rounded-xl border border-border/60">
              {list.slice(0, 15).map((e) => {
                const liberado = new Date(e.available_at).getTime() <= now
                const status = e.status === 'paid' ? 'Pago'
                  : e.status === 'reversed' ? 'Estornado ao aluno'
                  : e.status === 'processing' ? 'Em envio'
                  : liberado ? 'Disponível' : `Libera em ${format(parseISO(e.available_at), "d/MM 'às' HH:mm", { locale: ptBR })}`
                return (
                  <li key={e.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <p className="text-gray-900">{EARNING_REASON_LABELS[e.reason]}</p>
                      <p className="text-xs text-muted-foreground">{format(parseISO(e.created_at), "d 'de' MMM", { locale: ptBR })} · {status}</p>
                    </div>
                    <span className={cn('font-semibold', e.status === 'reversed' ? 'text-gray-400 line-through' : 'text-gray-900')}>
                      {formatCurrency(e.amount)}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function Resumo({ icon, label, valor, hint }: { icon: React.ReactNode; label: string; valor: number; hint?: string }) {
  return (
    <div className="rounded-xl bg-gray-50 p-3">
      <p className="text-xs text-gray-600 flex items-center gap-1.5">{icon}{label}</p>
      <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(valor)}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  )
}
